/**
 * PeakTrophyOverlay — seats the rotating 3D trophy (TrophyGlb3D) on the mountain
 * peak, rendered as an absolutely-positioned HTML overlay OUTSIDE the SVG.
 *
 * Why not a <foreignObject> inside the SVG: react-three-fiber can't measure a
 * container that lives inside a viewBox-scaled <foreignObject> (ResizeObserver
 * doesn't fire), so the WebGL canvas falls back to its 300x150 default and the
 * trophy renders mis-sized / off the peak. As a normal positioned div the canvas
 * measures correctly.
 *
 * It tracks the peak by mapping the peak's viewBox point through the SVG's own
 * screen transform, so it stays glued to the tip at any card size. Size scales
 * with the rendered mountain so it looks the same on the small Company View card
 * and the larger Dashboard card.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import TrophyGlb3D from "./TrophyGlb3D";

const PeakTrophyOverlay = ({
  svgRef,
  peakX = 483,
  peakY = 173,
  vbSize = 150, // trophy box size in viewBox units (scales with the mountain)
  // vertical anchor: >0.85 lifts the trophy so its base HANGS above the peak
  // with a clear gap — the mountain tip stays fully visible and the trophy
  // floats above it (it never rests on or covers the mountain).
  baseFactor = 1.12,
}) => {
  const ref = useRef(null);
  const [box, setBox] = useState(null);

  useLayoutEffect(() => {
    let lastKey = "";
    let pollTimer = 0;
    let tries = 0;

    // Returns true only when it accepted a valid measurement.
    const compute = () => {
      const svg = svgRef && svgRef.current;
      const el = ref.current;
      if (!svg || !el || !svg.getScreenCTM) return false;
      const host = el.offsetParent;
      if (!host) return false;
      const ctm = svg.getScreenCTM();
      if (!ctm) return false;
      // Reject transient/bad readings. compute() runs repeatedly after load; if
      // one fires mid-layout while the SVG is momentarily 0-width or unscaled,
      // ctm.a collapses toward 0 and the trophy would shrink to its 48px floor or
      // jump off-screen. Only accept a reading where the mountain is laid out.
      const svgRect = svg.getBoundingClientRect();
      if (!(svgRect.width > 40) || !Number.isFinite(ctm.a) || Math.abs(ctm.a) < 0.05) return false;
      const pt = svg.createSVGPoint();
      pt.x = peakX;
      pt.y = peakY;
      const s = pt.matrixTransform(ctm);
      const hostRect = host.getBoundingClientRect();
      // Size off the viewBox→screen scale (ctm.a = px per viewBox unit) so the
      // trophy scales with the mountain regardless of card size or viewBox padding.
      // Floor kept low: at phone widths a 48px floor left the trophy oversized
      // relative to the shrunken peak, hovering visibly above it (audit).
      const size = Math.max(34, Math.round(vbSize * Math.abs(ctm.a)));
      const left = Math.round(s.x - hostRect.left);
      const top = Math.round(s.y - hostRect.top);
      const key = `${left},${top},${size}`;
      // Only update when the placement actually changed — a repeat reading with
      // the same value must not re-render (that's what caused the flicker).
      if (key !== lastKey) {
        lastKey = key;
        setBox({ left, top, size });
      }
      return true;
    };

    // Poll until we get ONE valid measurement (so the trophy always shows even
    // if the first frames are mid-layout), then stop polling. The guard rejects
    // bad transients and the key-compare skips no-op updates, so later reruns
    // (resize) can never shrink or hide the placed trophy.
    const poll = () => {
      const ok = compute();
      tries += 1;
      if (!ok && tries < 40) pollTimer = setTimeout(poll, 100);
    };
    const raf = requestAnimationFrame(poll);
    const svg = svgRef && svgRef.current;
    const ro = svg && window.ResizeObserver ? new ResizeObserver(compute) : null;
    if (ro) ro.observe(svg);
    window.addEventListener("resize", compute);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(pollTimer);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [svgRef, peakX, peakY, vbSize]);

  const size = box ? box.size : 96;
  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        left: box ? box.left : -9999,
        top: box ? box.top : -9999,
        width: size,
        height: size,
        transform: `translate(-50%, -${baseFactor * 100}%)`,
        pointerEvents: "none",
        zIndex: 1300,
      }}
    >
      <TrophyGlb3D size="100%" />
    </div>
  );
};

export default PeakTrophyOverlay;
